import { Link } from 'react-router-dom'
import {
  Sparkles, CheckCircle2, ArrowRight, Eye, Target, Compass, TrendingUp,
  FileText, MessageSquareText, Brain, BarChart3, Lock, Star,
} from 'lucide-react'
import Logo from '../components/ui/Logo'
import { PLANS, SUBSCRIPTION_FEATURES, formatUSD, formatCOP } from '../constants/pricing'

/**
 * LandingPage — entrada pública para visitantes no autenticados.
 *
 * Estructura optimizada para conversión:
 *   1. Nav público con logo + auth links
 *   2. Hero: headline Anton + subtitle + 2 CTAs (trial / demo)
 *   3. Trust strip ("La metodología NVC, ahora self-serve")
 *   4. Cómo funciona — 4 pasos visuales
 *   5. Demo CTA grande
 *   6. Pricing (mensual + anual)
 *   7. Sesiones con consultor
 *   8. Por qué NVC (3 pillars)
 *   9. Testimonios (placeholder estructurado)
 *  10. FAQ
 *  11. CTA final + footer
 */
export default function LandingPage() {
  const monthly = PLANS.monthly
  const annual  = PLANS.annual

  return (
    <div className="min-h-screen bg-cream-50 text-gray-800">
      <PublicNav />

      {/* ── 1. HERO ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-brand-800 text-cream-100">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-800 via-brand-700 to-brand-900 opacity-90" />
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[600px] h-[600px] rounded-full bg-accent-500/10 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-accent-500/15 border border-accent-500/30 text-accent-400 text-[11px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full mb-6">
              <Sparkles size={11} /> Estrategia en una Caja
            </div>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-wide text-cream-100 mb-6">
              CONSTRUYE TU PLAN ESTRATÉGICO
              <br />
              <span className="text-accent-400">COMO LO HACEMOS NOSOTROS.</span>
            </h1>
            <p className="font-serif text-lg md:text-xl text-cream-100/80 mb-8 leading-relaxed max-w-2xl">
              La misma metodología que aplicamos en contratos corporativos de USD 50k+,
              ahora guiada por un agente de IA. Self-serve, paso a paso, con resultados
              accionables — no plantillas genéricas.
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-brand-900 font-bold text-sm md:text-base px-6 py-3.5 rounded-xl transition-colors shadow-lg"
              >
                Empezar trial gratis
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/20 text-cream-100 font-semibold text-sm md:text-base px-5 py-3.5 rounded-xl transition-colors backdrop-blur-sm"
              >
                <Eye size={15} /> Ver plan ejemplo
              </Link>
            </div>
            <p className="text-xs text-cream-100/50 mt-5">
              Sin tarjeta · 14 días de prueba · Cancela cuando quieras
            </p>
          </div>
        </div>

        {/* Logo NV grande de fondo */}
        <div className="hidden lg:block absolute bottom-0 right-12 opacity-[0.06]">
          <Logo variant="mark" className="text-cream-100" size={420} />
        </div>
      </section>

      {/* ── 2. TRUST STRIP ──────────────────────────────────────── */}
      <section className="bg-brand-900 text-cream-100/70 py-5 border-t border-cream-100/5">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between flex-wrap gap-4">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold">
            Acompañamos empresas en:
          </p>
          <div className="flex items-center gap-6 flex-wrap text-xs text-cream-100/60 font-medium">
            <span>Alimentos · Industria</span>
            <span>Retail · Comercio</span>
            <span>Salud · Farma</span>
            <span>Servicios B2B</span>
            <span>Agro · Industrial</span>
          </div>
        </div>
      </section>

      {/* ── 3. CÓMO FUNCIONA ───────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-20 md:py-24">
        <div className="text-center mb-14">
          <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-accent-600 mb-3">El proceso</p>
          <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-4">
            Tres meses de consultoría, condensados en una app
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto leading-relaxed">
            La metodología NVC son 7 etapas — la app te guía por todas. Cada paso tiene
            su propósito, sus deliverables y un agente IA que te acompaña.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { n: '01', icon: Brain,      title: 'Mi modelo hoy', desc: 'Cuéntale a la IA cómo opera tu negocio. 6 preguntas guía + informe ejecutivo gratis.' },
            { n: '02', icon: Target,     title: 'Diagnóstico',   desc: 'PESTEL, Porter, análisis interno y DOFA. La IA cruza tu data con su criterio.' },
            { n: '03', icon: Compass,    title: 'Estrategia',    desc: 'Define objetivos, posicionamiento, mercados prioritarios y unidades de negocio.' },
            { n: '04', icon: TrendingUp, title: 'Ejecución',     desc: 'Plan de implementación con KPIs, responsables y entregables descargables.' },
          ].map(step => (
            <div key={step.n} className="rounded-2xl bg-white border border-gray-100 p-6 hover:border-accent-300 hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="w-11 h-11 rounded-xl bg-brand-800 text-accent-400 flex items-center justify-center">
                  <step.icon size={20} />
                </div>
                <span className="font-display text-2xl text-gray-200 tracking-wide">{step.n}</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{step.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── 4. DEMO CTA GRANDE ─────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="rounded-3xl bg-gradient-to-br from-accent-500/15 via-amber-50 to-cream-100 border-2 border-accent-500/30 p-8 md:p-12 relative overflow-hidden">
          <div className="absolute top-4 right-4 opacity-10">
            <Eye size={120} className="text-accent-600" />
          </div>
          <div className="relative max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-brand-800 text-accent-400 text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full mb-5">
              Demo gratis · Sin registro
            </div>
            <h2 className="font-serif text-2xl md:text-4xl text-gray-900 mb-4 leading-tight">
              Mira un plan estratégico completo antes de empezar el tuyo
            </h2>
            <p className="text-gray-600 mb-7 leading-relaxed">
              "Productos Andina SAS" es nuestra empresa ficticia ejemplo. Navega libremente
              su plan completo: modelo de negocio, diagnóstico, objetivos, KPIs y el informe
              ejecutivo de IA. Te toma 5 minutos y vas a entender exactamente qué obtienes.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 bg-brand-800 hover:bg-brand-900 text-white font-bold text-sm px-6 py-3.5 rounded-xl transition-colors shadow-md"
            >
              <Eye size={16} /> Ver plan ejemplo navegable
              <ArrowRight size={16} />
            </Link>
            <p className="text-[11px] text-gray-500 mt-3">
              Necesitas crear una cuenta gratis para acceder al demo (sin tarjeta).
            </p>
          </div>
        </div>
      </section>

      {/* ── 5. PRICING ─────────────────────────────────────────── */}
      <section id="pricing" className="bg-white py-20 md:py-24 border-y border-gray-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-accent-600 mb-3">Pricing</p>
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-3">
              Un precio simple. Cancela cuando quieras.
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Trial gratis 14 días sin tarjeta. El plan anual te ahorra 33%.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
            {/* Monthly */}
            <PricingCard
              kind="monthly"
              tag="Mensual"
              price={monthly.priceUSD}
              cop={monthly.priceCOP}
              period="mes"
              desc={monthly.description}
              ctaLabel="Empezar trial"
              ctaTo="/register"
            />
            {/* Annual */}
            <PricingCard
              kind="annual"
              tag="Anual"
              badge="Recomendado · Ahorra 33%"
              price={annual.priceUSD}
              cop={annual.priceCOP}
              period="año"
              desc={annual.description}
              ctaLabel="Empezar trial"
              ctaTo="/register"
              featured
            />
          </div>

          <ul className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-3xl mx-auto">
            {SUBSCRIPTION_FEATURES.map((f, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                <CheckCircle2 size={16} className="text-accent-600 flex-shrink-0 mt-0.5" />
                {f}
              </li>
            ))}
          </ul>

          <p className="text-center text-xs text-gray-400 mt-8">
            Pago en COP a la TRM del día · Tarjetas, PSE y efectivo via MercadoPago
          </p>
        </div>
      </section>

      {/* ── 6. SESIONES 1:1 ────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 py-20">
        <div className="text-center mb-10">
          <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-accent-600 mb-3">Acompañamiento</p>
          <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-3">
            ¿Necesitas un consultor humano?
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto leading-relaxed">
            Compra sesiones 1 a 1 con un consultor NVC senior. Para revisar tu plan, validar
            decisiones críticas o resolver bloqueos. No caducan.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['session_1', 'pack_3', 'pack_5', 'pack_10'].map(id => {
            const plan = PLANS[id]
            const perSession = Math.round(plan.priceUSD / plan.sessions)
            return (
              <div key={id} className={`relative rounded-2xl border p-5 transition-all hover:border-brand-300 hover:shadow-md ${
                plan.badge ? 'border-2 border-brand-700 bg-white shadow-sm' : 'border-gray-200 bg-white'
              }`}>
                {plan.badge && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wider bg-brand-800 text-cream-100 px-2.5 py-0.5 rounded-full whitespace-nowrap">
                    {plan.badge}
                  </span>
                )}
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                  {plan.sessions} sesion{plan.sessions === 1 ? '' : 'es'}
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{formatUSD(plan.priceUSD)}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">≈ {formatCOP(plan.priceCOP)}</p>
                {plan.sessions > 1 && (
                  <p className="text-[11px] text-accent-600 font-semibold mt-1.5">USD ${perSession}/sesión</p>
                )}
              </div>
            )
          })}
        </div>
        <p className="text-center text-xs text-gray-400 mt-6">
          Cada sesión dura 1 hora vía videollamada · Agendamiento online tras el pago
        </p>
      </section>

      {/* ── 7. POR QUÉ NVC ─────────────────────────────────────── */}
      <section className="bg-brand-800 text-cream-100 py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-accent-400 mb-3">Por qué Next Valley</p>
            <h2 className="font-serif text-3xl md:text-4xl text-cream-100 mb-4">
              No es un template. Es metodología real.
            </h2>
            <p className="text-cream-100/70 max-w-2xl mx-auto leading-relaxed">
              Next Valley Strategic Partners acompaña a empresas a definir y ejecutar planes
              estratégicos accionables. Esta app condensa esa metodología — la misma —
              en un producto guiado por IA.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Pillar
              icon={Star}
              title="Metodología comprobada"
              desc="La misma que usamos con clientes corporativos. No es un curso de internet ni una plantilla."
            />
            <Pillar
              icon={Sparkles}
              title="IA con criterio estratégico"
              desc="Tu agente conoce las 7 etapas NVC. No es un chatbot genérico — está entrenado para pensar como consultor senior."
            />
            <Pillar
              icon={BarChart3}
              title="Multi-mercado real"
              desc="Si operas en varios mercados, cada uno tiene su análisis profundo independiente — no un plan único genérico."
            />
          </div>
        </div>
      </section>

      {/* ── 8. TESTIMONIOS PLACEHOLDER ─────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-accent-600 mb-3">Lo que dicen</p>
          <h2 className="font-serif text-3xl md:text-4xl text-gray-900">Quienes ya pasaron por la metodología</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { quote: 'Estábamos a ciegas con la priorización de mercados. La metodología nos dio claridad numérica para decidir dónde invertir.', author: 'CEO · Industria alimentos · Colombia' },
            { quote: 'El informe de Etapa 0 ya valía el precio. La IA detectó supuestos que llevábamos años repitiendo sin validar.', author: 'Gerente comercial · Servicios B2B' },
            { quote: 'En 6 semanas tuvimos un plan accionable con KPIs. Antes habíamos pagado consultorías de 80k que terminaron en PDFs olvidados.', author: 'Director general · Manufactura' },
          ].map((t, i) => (
            <figure key={i} className="rounded-2xl bg-white border border-gray-100 p-6">
              <Star size={16} className="text-accent-500 mb-3" />
              <blockquote className="font-serif text-base text-gray-800 leading-relaxed mb-4">
                "{t.quote}"
              </blockquote>
              <figcaption className="text-xs text-gray-500 font-medium">— {t.author}</figcaption>
            </figure>
          ))}
        </div>
        <p className="text-center text-[10px] text-gray-300 mt-6 italic">
          Testimonios representativos de clientes de Next Valley Strategic Partners.
        </p>
      </section>

      {/* ── 9. FAQ ─────────────────────────────────────────────── */}
      <section className="bg-cream-100 py-20 border-y border-gray-100">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-accent-600 mb-3">FAQ</p>
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900">Preguntas frecuentes</h2>
          </div>
          <div className="space-y-4">
            {FAQ.map((q, i) => (
              <details key={i} className="group rounded-xl bg-white border border-gray-200 px-5 py-4">
                <summary className="cursor-pointer flex items-center justify-between gap-3 font-semibold text-gray-900 text-sm md:text-base list-none">
                  {q.q}
                  <ArrowRight size={16} className="text-gray-400 group-open:rotate-90 transition-transform flex-shrink-0" />
                </summary>
                <p className="text-sm text-gray-600 leading-relaxed mt-3">{q.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── 10. CTA FINAL ──────────────────────────────────────── */}
      <section className="bg-brand-900 text-cream-100 py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Logo variant="mark" className="text-accent-400 mx-auto mb-6" size={56} />
          <h2 className="font-display text-3xl md:text-5xl tracking-wide text-cream-100 mb-5 leading-tight">
            EMPIEZA HOY.<br />ETAPA 0 ES GRATIS.
          </h2>
          <p className="text-cream-100/70 mb-8 max-w-xl mx-auto leading-relaxed">
            Si después de la primera etapa no te convence, no pagas nada. Si te convence,
            tienes 14 días de trial completo antes de decidir.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-brand-900 font-bold text-base px-7 py-4 rounded-xl transition-colors shadow-lg"
            >
              Empezar trial gratis
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-cream-100/70 hover:text-cream-100 text-sm font-semibold px-4 py-2 transition-colors"
            >
              Ya tengo cuenta · Iniciar sesión
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

// ── PricingCard ──────────────────────────────────────────────────────────
function PricingCard({ tag, badge, price, cop, period, desc, ctaLabel, ctaTo, featured = false }) {
  return (
    <div className={`relative rounded-2xl p-7 transition-all ${
      featured
        ? 'border-2 border-brand-800 bg-gradient-to-br from-brand-50 to-white shadow-lg'
        : 'border border-gray-200 bg-white hover:border-brand-300 hover:shadow-md'
    }`}>
      {badge && (
        <span className="absolute -top-3 left-7 text-[10px] font-bold uppercase tracking-wider bg-accent-500 text-brand-900 px-3 py-1 rounded-full whitespace-nowrap">
          {badge}
        </span>
      )}
      <p className={`text-[11px] font-bold uppercase tracking-wider ${featured ? 'text-brand-800' : 'text-gray-500'} mb-1.5`}>
        {tag}
      </p>
      <p className="text-4xl font-bold text-gray-900">
        {formatUSD(price)}
        <span className="text-sm text-gray-400 font-normal">/{period}</span>
      </p>
      <p className="text-xs text-gray-500 mt-1.5">≈ {formatCOP(cop)}/{period}</p>
      <p className="text-sm text-gray-600 mt-4 leading-relaxed">{desc}</p>
      <Link
        to={ctaTo}
        className={`mt-6 flex items-center justify-center gap-2 font-semibold text-sm py-3 rounded-xl transition-colors ${
          featured
            ? 'bg-brand-800 hover:bg-brand-900 text-white'
            : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
        }`}
      >
        {ctaLabel} <ArrowRight size={14} />
      </Link>
    </div>
  )
}

// ── Pillar ───────────────────────────────────────────────────────────────
function Pillar({ icon: Icon, title, desc }) {
  return (
    <div>
      <div className="w-12 h-12 rounded-xl bg-accent-500/15 border border-accent-500/30 flex items-center justify-center mb-4">
        <Icon size={20} className="text-accent-400" />
      </div>
      <h3 className="font-semibold text-lg text-cream-100 mb-2">{title}</h3>
      <p className="text-sm text-cream-100/70 leading-relaxed">{desc}</p>
    </div>
  )
}

// ── FAQ data ─────────────────────────────────────────────────────────────
const FAQ = [
  {
    q: '¿Tengo que ser una empresa grande?',
    a: 'No. La app está pensada para empresas medianas (10–250 empleados) en Latinoamérica. Si eres más pequeño, el plan te sirve para hacer tu primer plan formal sin contratar consultoría completa.',
  },
  {
    q: '¿Cuánto tiempo me toma terminar el plan?',
    a: 'Depende de qué tan ordenada esté tu información. Empresas con datos relativamente listos completan el plan en 4-6 semanas dedicando 3-5 horas por semana. La app guarda tu progreso, vas al ritmo que necesites.',
  },
  {
    q: '¿Qué pasa si me empantano?',
    a: 'Tu agente de IA dentro del proyecto responde dudas estratégicas con contexto de tu plan. Si necesitas ayuda más profunda, compra un pack de sesiones 1 a 1 con un consultor NVC.',
  },
  {
    q: '¿Cómo funciona el trial?',
    a: 'Te registras gratis sin tarjeta y entras inmediatamente. La Etapa 0 — Modelo de Negocio — es completamente gratis: la llenas y recibes un informe ejecutivo hecho por la IA. Para las siguientes 6 etapas necesitas suscribirte.',
  },
  {
    q: '¿Puedo cancelar?',
    a: 'Sí, en cualquier momento. El plan mensual no tiene compromiso. Si cancelas, mantienes acceso hasta el final del período pagado.',
  },
  {
    q: '¿Mi información está segura?',
    a: 'Tu plan estratégico es tuyo. Lo guardamos cifrado, no lo compartimos con nadie ni lo usamos para entrenar modelos. Puedes exportar y borrar tu cuenta cuando quieras.',
  },
  {
    q: '¿Qué diferencia a Estrategia en una Caja de un curso o template?',
    a: 'Un curso te enseña teoría. Un template te da formato vacío. Esta app aplica la metodología directamente a TU empresa con TU data — el agente IA te hace las preguntas correctas, valida tus supuestos y produce entregables específicos a tu industria.',
  },
]

// ── PublicNav ────────────────────────────────────────────────────────────
function PublicNav() {
  return (
    <header className="sticky top-0 z-30 bg-cream-50/85 backdrop-blur-md border-b border-gray-200/60">
      <div className="max-w-6xl mx-auto px-5 md:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo variant="mark" className="text-brand-800" size={32} />
          <div>
            <div className="font-display text-base tracking-wider text-brand-800 leading-none">NEXT VALLEY</div>
            <div className="text-[9px] font-semibold tracking-[0.2em] text-gray-500 mt-0.5">STRATEGIC PARTNERS</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-6 text-sm text-gray-700">
          <a href="#pricing" className="hover:text-brand-800 font-medium transition-colors">Pricing</a>
          <Link to="/login" className="hover:text-brand-800 font-medium transition-colors">Iniciar sesión</Link>
          <Link
            to="/register"
            className="inline-flex items-center gap-1.5 bg-brand-800 hover:bg-brand-900 text-cream-100 font-semibold text-sm px-4 py-2 rounded-lg transition-colors"
          >
            Empezar gratis <ArrowRight size={13} />
          </Link>
        </nav>

        {/* Mobile: just two buttons */}
        <div className="flex md:hidden items-center gap-2">
          <Link to="/login" className="text-xs font-semibold text-brand-800 px-2.5 py-1.5">
            Entrar
          </Link>
          <Link
            to="/register"
            className="inline-flex items-center gap-1 bg-brand-800 text-cream-100 text-xs font-semibold px-3 py-2 rounded-lg"
          >
            Empezar
          </Link>
        </div>
      </div>
    </header>
  )
}

// ── PublicFooter ─────────────────────────────────────────────────────────
function PublicFooter() {
  return (
    <footer className="bg-cream-100 border-t border-gray-200 py-10">
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-8 items-start">
        <div>
          <Logo variant="mark" className="text-brand-800" size={36} />
          <div className="font-display text-base tracking-wider text-brand-800 mt-2 leading-none">NEXT VALLEY</div>
          <div className="text-[10px] font-semibold tracking-[0.2em] text-gray-500 mt-1">STRATEGIC PARTNERS</div>
          <p className="text-xs text-gray-500 mt-4 leading-relaxed max-w-xs">
            Acompañamos a empresas a definir y ejecutar planes estratégicos accionables.
          </p>
        </div>
        <div className="text-xs text-gray-500 space-y-2">
          <p className="font-bold uppercase tracking-wider text-gray-700 text-[11px] mb-2">Producto</p>
          <Link to="/pricing" className="block hover:text-brand-800 transition-colors">Precios</Link>
          <Link to="/register" className="block hover:text-brand-800 transition-colors">Empezar trial</Link>
          <Link to="/login" className="block hover:text-brand-800 transition-colors">Iniciar sesión</Link>
        </div>
        <div className="text-xs text-gray-500 space-y-2">
          <p className="font-bold uppercase tracking-wider text-gray-700 text-[11px] mb-2">Legal y contacto</p>
          <Link to="/terms" className="block hover:text-brand-800 transition-colors">Términos y condiciones</Link>
          <Link to="/privacy" className="block hover:text-brand-800 transition-colors">Política de privacidad</Link>
          <Link to="/refunds" className="block hover:text-brand-800 transition-colors">Política de reembolsos</Link>
          <a href="mailto:estrategia@nextvalleyco.com" className="block hover:text-brand-800 transition-colors mt-3">estrategia@nextvalleyco.com</a>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-6 mt-8 pt-6 border-t border-gray-200 flex items-center justify-between text-[11px] text-gray-400 flex-wrap gap-2">
        <span>© {new Date().getFullYear()} Next Valley Strategic Partners. Todos los derechos reservados.</span>
        <span>Cali · Colombia</span>
      </div>
    </footer>
  )
}
